import React from 'react';
import { AssetUrls } from 'config/assets';
import './PriceRangeFilter.css';

/**
 * Min/Max price (point-value) range inputs. Shared between the Send page toolbar
 * and the Trade Alerts filter row so both surfaces use the same convention.
 *
 * Values are non-negative integers; commas are stripped on input and any value
 * <= 0 (or non-numeric) is treated as "unset" (`undefined`).
 *
 * NOTE: the markup keeps the `send-price-*` class names so the Send page styling
 * is identical to before this was extracted out of `sendToolbarControls.tsx`.
 *
 * `variant='connected'` is an opt-in compact layout (one bordered container, a single
 * `groupLabel`, both inputs split by a center divider). The default `'separate'` variant
 * renders exactly as before, so the Send page and Trade Alerts are unaffected.
 */
export function PriceRangeFilter({
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
  minLabel = 'Price (min.)',
  maxLabel = 'Price (max.)',
  variant = 'separate',
  groupLabel = 'Price',
}: {
  priceMin?: number;
  priceMax?: number;
  onPriceMinChange?: (value: number | undefined) => void;
  onPriceMaxChange?: (value: number | undefined) => void;
  /** Field labels — override to repurpose the control (e.g. "Trader balance (min.)"). */
  minLabel?: string;
  maxLabel?: string;
  /** Layout: two separate labeled boxes (default) or one connected min–max box. */
  variant?: 'separate' | 'connected';
  /** Single label shown once above the connected variant (e.g. "Trader balance"). */
  groupLabel?: string;
}) {
  // Shared parse logic: strip commas, treat non-numeric / <= 0 as "unset".
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/,/g, '');
    const num = parseInt(val, 10);
    onPriceMinChange?.(isNaN(num) || num <= 0 ? undefined : num);
  };
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/,/g, '');
    const num = parseInt(val, 10);
    onPriceMaxChange?.(isNaN(num) || num <= 0 ? undefined : num);
  };

  const coinIcon = (
    <img
      src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
      className="send-price-coin-icon"
      alt=""
    />
  );

  if (variant === 'connected') {
    return (
      <div className="send-price-filter send-price-filter--connected">
        <span className="send-price-group-label">{groupLabel}</span>
        <div className="send-price-connected">
          {coinIcon}
          <input
            type="text"
            className="send-price-input send-price-input--min"
            placeholder="0"
            aria-label={minLabel}
            value={priceMin ?? ''}
            onChange={handleMinChange}
          />
          <span className="send-price-connected-divider" aria-hidden="true">–</span>
          <input
            type="text"
            className="send-price-input send-price-input--max"
            placeholder="Any"
            aria-label={maxLabel}
            value={priceMax ?? ''}
            onChange={handleMaxChange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="send-price-filter">
      <div className="send-price-field">
        <label>{minLabel}</label>
        <div className="send-price-input-wrapper">
          {coinIcon}
          <input
            type="text"
            className="send-price-input"
            placeholder="0"
            value={priceMin ?? ''}
            onChange={handleMinChange}
          />
        </div>
      </div>
      <div className="send-price-field">
        <label>{maxLabel}</label>
        <div className="send-price-input-wrapper">
          {coinIcon}
          <input
            type="text"
            className="send-price-input"
            placeholder="999,999"
            value={priceMax ?? ''}
            onChange={handleMaxChange}
          />
        </div>
      </div>
    </div>
  );
}

export default PriceRangeFilter;
